// 배경 비트맵 피라미드(원본·1/2·1/4) — 워커 클라이언트와 배율별로 레벨을 골라 그리는 Konva.Image.
// 맞춤 배율에서 원본을 그리면 GPU 디코드 캐시가 그 배율용 축소본을 메인 스레드에서 만든다(A21s 층 진입 ≈180ms).
// 워커가 미리 만든 축소본을 그리면 메인 스레드에선 작은 텍스처 업로드만 남는다.
import Konva from 'konva'
import type { Context } from 'konva/lib/Context'

export interface BitmapPyramid {
  full: ImageBitmap
  half: ImageBitmap
  quarter: ImageBitmap
}

type Reply = {
  id: number
  full?: ImageBitmap
  quarter?: ImageBitmap
  half?: ImageBitmap
  error?: string
}

// 원본 디코드(A21s ≈310ms)를 포함한 상한 — 넘으면 호출자가 메인 스레드 비트맵 경로로
const REPLY_TIMEOUT_MS = 15000

let worker: Worker | null = null
let workerBroken = false
let nextId = 0
const pending = new Map<number, (reply: Reply) => void>()

function failAll(): void {
  workerBroken = true
  worker?.terminate()
  worker = null
  for (const resolve of pending.values()) resolve({ id: -1, error: 'worker error' })
  pending.clear()
}

function getWorker(): Worker | null {
  if (workerBroken) return null
  if (worker) return worker
  try {
    worker = new Worker(new URL('./bitmapPyramid.worker.ts', import.meta.url), { type: 'module' })
  } catch {
    failAll()
    return null
  }
  worker.onmessage = (e: MessageEvent<Reply>): void => {
    const reply = e.data
    const resolve = pending.get(reply.id)
    if (!resolve) {
      // 시간 초과로 버린 요청의 늦은 결과
      reply.full?.close()
      reply.half?.close()
      reply.quarter?.close()
      return
    }
    pending.delete(reply.id)
    resolve(reply)
  }
  worker.onerror = failAll
  return worker
}

/** 워커를 못 쓰거나 실패면 null — 호출자가 메인 스레드 createImageBitmap 경로로 */
export function buildBitmapPyramid(blob: Blob): Promise<BitmapPyramid | null> {
  if (typeof OffscreenCanvas === 'undefined') return Promise.resolve(null)
  const w = getWorker()
  if (!w) return Promise.resolve(null)
  const id = ++nextId
  return new Promise(resolve => {
    const timer = setTimeout(() => {
      if (!pending.delete(id)) return
      resolve(null)
    }, REPLY_TIMEOUT_MS)
    pending.set(id, reply => {
      clearTimeout(timer)
      if (reply.error || !reply.full || !reply.half || !reply.quarter) {
        reply.full?.close()
        reply.half?.close()
        reply.quarter?.close()
        resolve(null)
        return
      }
      resolve({ full: reply.full, half: reply.half, quarter: reply.quarter })
    })
    w.postMessage({ id, blob })
  })
}

// 제스처(핀치·팬·휠) 중인 스테이지 — 이 동안 피라미드 이미지는 제스처 직전에 그린 레벨을 유지한다.
// 장치 배율 0.5·0.25 경계에서 레벨이 바뀌면 선 굵기·진하기가 한 프레임에 바뀌어 핀치 중 도면이 튀어 보인다
// (A21s 맞춤 배율 × dpr = 0.21~0.36이라 평소 핀치가 경계를 지남). 손을 떼면(resumeHit 다시 그리기) 맞는 레벨로 바꾼다 — 타일과 같은 방식
const gestureStages = new WeakSet<Konva.Stage>()

export function setPyramidGesture(stage: Konva.Stage, active: boolean): void {
  if (active) gestureStages.add(stage)
  else gestureStages.delete(stage)
}

/**
 * 피라미드를 쓰는 배경 이미지. `image` 속성은 늘 원본(크기·파괴 시 close 대상), 그릴 때만 장치 배율에 맞는 레벨로 바꿔 그린다.
 * width/height는 기본값을 원본 치수로 명시한다(그리기 중 바꾼 레벨 비트맵 크기를 기본값으로 쓰지 않도록).
 */
export class KonvaPyramidImage extends Konva.Image {
  private pyramid: BitmapPyramid | undefined
  /** 마지막으로 그린 레벨 — 제스처 중에는 이것을 계속 그린다 */
  private drawnLevel: ImageBitmap | undefined

  constructor(config: Konva.ImageConfig, pyramid: BitmapPyramid) {
    super({
      width: pyramid.full.width,
      height: pyramid.full.height,
      ...config,
      image: pyramid.full,
    })
    this.pyramid = pyramid
  }

  _sceneFunc(context: Context): void {
    const pyramid = this.pyramid
    const full = this.attrs.image
    if (!pyramid || full !== pyramid.full) {
      super._sceneFunc(context)
      return
    }
    // 장치 배율(스테이지 배율 × 캔버스 pixelRatio × 회전 무관) — 레벨 크기가 그려질 크기 이상인 가장 작은 레벨
    const m = (context as unknown as { _context: CanvasRenderingContext2D })._context.getTransform()
    const scale = Math.hypot(m.a, m.b)
    let level = scale <= 0.25 ? pyramid.quarter : scale <= 0.5 ? pyramid.half : full
    const stage = this.getStage()
    if (this.drawnLevel && stage && gestureStages.has(stage)) {
      level = this.drawnLevel
    }
    this.drawnLevel = level
    if (level === full) {
      // 원본 레벨을 장치 배율 1 미만으로 그릴 땐 보간 품질 'medium' — 손 뗄 때 레벨 전환 계단을 줄이고(A21s 2.2→1.5배)
      // 축소 선의 톱니를 덜어 준다(그리기·프레임 비용 차 없음)
      if (scale >= 1) {
        super._sceneFunc(context)
        return
      }
      const ctx = (context as unknown as { _context: CanvasRenderingContext2D })._context
      const prev = ctx.imageSmoothingQuality
      ctx.imageSmoothingQuality = 'medium'
      try {
        super._sceneFunc(context)
      } finally {
        ctx.imageSmoothingQuality = prev
      }
      return
    }
    // _setAttr를 거치지 않는다(그리기 중 재그리기 예약 방지)
    this.attrs.image = level
    try {
      super._sceneFunc(context)
    } finally {
      this.attrs.image = full
    }
  }

  destroy(): this {
    const pyramid = this.pyramid
    this.pyramid = undefined
    this.drawnLevel = undefined
    // 원본은 호출자(배경 교체·엔진 파괴)가 image()로 받아 닫는다
    pyramid?.quarter.close()
    pyramid?.half.close()
    return super.destroy()
  }
}
